import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { MaskText, SectionLabel } from './ui/Reveal';
import { MagneticButton } from './ui/MagneticButton';
import { services } from '../data/site';
import { useHasFinePointer } from '../hooks/useMedia';
type Props = {
  /** Show only the first N services (homepage preview). */
  limit?: number;
  /** Append the "View All Services" link to the full Services page. */
  withCta?: boolean;
};

/**
 * Interactive services index.
 * Desktop: hovering a row reveals the related visual in a sticky panel.
 * Mobile: rows expand into an accordion with the description + visual.
 */
export function ServicesSection({
  limit,
  withCta = false
}: Props) {
  const list = limit ? services.slice(0, limit) : services;
  const [active, setActive] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const fine = useHasFinePointer();
  return <section className="bg-surface py-20 sm:py-28" aria-label="Services" data-id="element-487" data-mp="1smlhszb7182b">
      <div className="mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-488" data-mp="2plgdlrr8mrhf">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end" data-id="element-489" data-mp="2kn53rh0y0gz7">
          <div data-id="element-490" data-mp="2ynpm6c2y4as6">
            <SectionLabel data-id="element-491" data-mp="2yo97m2rt1hay">Services</SectionLabel>
            <h2 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tightest sm:text-7xl" data-id="element-492" data-mp="2ylvbdqle1z1t">
              <MaskText text="What We|Create" data-id="element-493" data-mp="26wjv0l57dnf1" />
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-muted sm:text-lg" data-id="element-494" data-mp="26ufgzadlb811">
            From identity to execution, we create visual systems that help businesses communicate
            with clarity and confidence.
          </p>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-495" data-mp="2axc3vda5xqas">
          <ul className="border-t border-line" data-id="element-496" data-mp="1n8kjv96q8e93">
            {list.map((service, index) => {
            const isOpen = openIndex === index;
            return <li key={service.title} className="border-b border-line" data-id="element-497" data-mp="1j54nmdykkfwx" data-mpi={index}>
                  <button type="button" className="group flex w-full items-center justify-between gap-4 py-5 text-left" onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} data-cursor="hover" data-id="element-498" data-mp="3ibftif9yi5uf">
                    <span className="flex items-baseline gap-4 sm:gap-6" data-id="element-499" data-mp="34by0fnpmo6f9">
                      <span className="label w-8 shrink-0 text-muted" data-id="element-500" data-mp="34eah7xjngaqq">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className={`font-display text-2xl font-bold uppercase leading-none tracking-tightest transition-colors duration-300 sm:text-4xl ${active === index ? 'text-brand' : 'text-ink group-hover:text-brand'}`} data-id="element-501" data-mp="3ebf6w7cpeoca">
                        {service.title}
                      </span>
                    </span>
                    <span className="flex items-center gap-4" data-id="element-502" data-mp="293w1cfr3ew41">
                      <span className="label hidden text-muted sm:inline" data-id="element-503" data-mp="24bm8aquolfxj">{service.category}</span>
                      <PlusIcon className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45 text-brand' : 'text-muted'}`} aria-hidden="true" data-id="element-504" data-mp="1ze2o7abay8xo" />
                    </span>
                  </button>

                  <AnimatePresence initial={false} data-id="element-505" data-mp="1lfma2yb41g5i">
                    {isOpen ? <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.45,
                  ease: [0.16, 1, 0.3, 1]
                }} className="overflow-hidden" data-id="element-506" data-mp="1gg9nmeo5ib3g">
                        <div className="pb-6 sm:pl-[3.5rem]" data-id="element-507" data-mp="2cflw6uernjmv">
                          <p className="max-w-lg text-sm leading-relaxed text-muted" data-id="element-508" data-mp="1yh4bz6o2kp9p">
                            {service.description}
                          </p>
                          {!fine ? <img src={service.image} alt={`${service.title} example`} className="mt-4 aspect-[16/10] w-full object-cover" loading="lazy" decoding="async" data-id="element-509" data-mp="1orb004k0m84x" /> : null}
                        </div>
                      </motion.div> : null}
                  </AnimatePresence>
                </li>;
          })}
          </ul>

          <div className="hidden lg:block" data-id="element-510" data-mp="1at4abbp7zegc">
            <div className="sticky top-28" data-id="element-511" data-mp="r4q1pzrb2xoi">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-surface2" data-id="element-512" data-mp="r7kklmuo8aft">
                <AnimatePresence mode="wait" data-id="element-513" data-mp="n5pkgloh833m">
                  <motion.img key={list[active].image + active} src={list[active].image} alt={`${list[active].title} example`} className="absolute inset-0 h-full w-full object-cover" initial={{
                  opacity: 0,
                  scale: 1.06
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1]
                }} loading="lazy" decoding="async" data-id="element-514" data-mp="1147344ziypeo" />
                </AnimatePresence>
              </div>
              <div className="mt-4 flex items-start justify-between gap-4" data-id="element-515" data-mp="1tsuu8h6yg905">
                <p className="max-w-xs text-sm leading-relaxed text-muted" data-id="element-516" data-mp="1ou1iat7bgq23">
                  {list[active].description}
                </p>
                <span className="label shrink-0 text-brand" data-id="element-517" data-mp="1vuddjio2v7dc">{list[active].category}</span>
              </div>
            </div>
          </div>
        </div>

        {withCta ? <div className="mt-14 flex flex-col items-start gap-4 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-518" data-mp="25tuke3f2fraw">
            <p className="max-w-md text-sm text-muted" data-id="element-519" data-mp="2fkfpm9p8vu2s">
              {list.length} of {services.length} disciplines shown. The full service list, process
              and deliverables live on the Services page.
            </p>
            <MagneticButton to="/services" cursorLabel="All services" showArrow data-id="element-520" data-mp="2okucvelrrj8x">
              View All Services
            </MagneticButton>
          </div> : null}
      </div>
    </section>;
}